import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { handleError } from '../services/ErrorService';
import { BillingPeriod, Price } from '../types';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getPrice(
  licenseCount: number,
  billingPeriod: BillingPeriod,
  allPrices: Price[],
): number {
  if (!allPrices || allPrices.length === 0) {
    console.error('Price calculation failed: No price data available', {
      licenseCount,
      billingPeriod,
      pricesCount: allPrices?.length || 0,
    });
    handleError(
      new Error(
        'Pricing information is currently unavailable. Please refresh the page.',
      ),
    );
    return -1;
  }

  // Find the price by matching lookup key pattern
  const billingPeriodMap: Record<BillingPeriod, string> = {
    month: 'monthly',
    '3month': '3monthly',
    year: 'yearly',
  };

  const stripeBillingPeriod = billingPeriodMap[billingPeriod];
  if (!stripeBillingPeriod) {
    console.error('Price calculation failed: Invalid billing period', {
      billingPeriod,
      licenseCount,
      validPeriods: Object.keys(billingPeriodMap),
    });
    handleError(new Error(`Invalid billing period: ${billingPeriod}`));
    return -1;
  }

  const lookupKey = `gaps-${stripeBillingPeriod}-${licenseCount}`;
  const price = allPrices.find((p) => p.lookup_key === lookupKey);

  if (!price) {
    console.error('Price calculation failed: No matching price found', {
      lookupKey,
      licenseCount,
      billingPeriod,
      availablePrices: allPrices.map((p) => ({
        id: p.id,
        lookup_key: p.lookup_key,
        unit_amount: p.unit_amount,
      })),
    });
    handleError(
      new Error(
        `Pricing for ${licenseCount} license${
          licenseCount > 1 ? 's' : ''
        } with ${billingPeriod} billing is not currently available. Please try a different option or contact support.`,
      ),
    );
    return -1;
  }

  if (!price.unit_amount) {
    console.error('Price calculation failed: Price has no amount', {
      priceId: price.id,
      lookupKey: price.lookup_key,
      unitAmount: price.unit_amount,
    });
    handleError(
      new Error(
        `Price information is incomplete. Please refresh the page or contact support.`,
      ),
    );
    return -1;
  }

  // Convert from cents to dollars
  return price.unit_amount / 100;
}

export function getPricePerLicense(
  licenseCount: number,
  billingPeriod: BillingPeriod,
  allPrices: Price[],
): number {
  return licenseCount === 1
    ? getPrice(licenseCount, billingPeriod, allPrices)
    : Math.round(
        (100 * getPrice(licenseCount, billingPeriod, allPrices)) / licenseCount,
      ) / 100;
}

export function getDiscountPercentage(
  licenseCount: number,
  billingPeriod: BillingPeriod,
  allPrices: Price[],
): number {
  if (!allPrices || allPrices.length === 0) {
    return 0;
  }

  const singleLicensePrice = getPrice(1, billingPeriod, allPrices);
  const multiLicensePrice = getPrice(licenseCount, billingPeriod, allPrices);

  if (singleLicensePrice <= 0 || multiLicensePrice <= 0) {
    return 0;
  }

  const normalPrice = singleLicensePrice * licenseCount;
  return Math.round((1 - multiLicensePrice / normalPrice) * 100);
}

type Amounts = {
  amount?: number;
  discount?: number;
  tax?: number;
  total?: number;
};

export function getTotal(amounts?: Amounts): number | undefined {
  if (!amounts?.amount) {
    return undefined;
  }
  let total = amounts.amount;
  if (amounts.discount) {
    total = total - amounts.discount;
  }
  if (amounts.tax) {
    total = total + amounts.tax;
  }
  return total;
}
